import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Chronicle = () => {
  const { t } = useLanguage();
  const milestones = t("chronicle.milestones");
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = milestones[active];

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % milestones.length);
    }, 5000);
    return () => clearInterval(id);
  }, [paused, active, milestones.length]);

  return (
    <section
      id="chronicle"
      data-testid="chronicle-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative py-28 sm:py-40 bg-[#0A1420] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-2xl"
        >
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-8">
            {t("chronicle.eyebrow")}
          </p>
          <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
            {t("chronicle.title")}
            <span className="gold-gradient-text italic">{t("chronicle.titleAccent")}</span>
          </h2>
          <p className="mt-6 text-[#A0ABC0] text-base sm:text-lg font-light leading-relaxed">
            {t("chronicle.subtitle")}
          </p>
        </motion.div>

        {/* Timeline rail */}
        <div className="mt-16 relative">
          <div className="absolute left-0 right-0 top-3 h-px bg-white/15" />
          <motion.div
            key={`${active}-${paused}`}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: paused ? 0 : 1 }}
            transition={{ duration: paused ? 0.3 : 5, ease: "linear" }}
            className="absolute left-0 right-0 top-3 h-px bg-[#D4AF37] origin-left"
            data-testid="chronicle-progress"
          />
          <div className="relative flex gap-8 sm:gap-0 sm:justify-between overflow-x-auto pb-4 no-scrollbar">
            {milestones.map((m, i) => {
              const isActive = i === active;
              return (
                <button
                  key={m.year}
                  onClick={() => setActive(i)}
                  data-testid={`chronicle-marker-${i}`}
                  className="group flex-shrink-0 flex flex-col items-center text-center focus:outline-none"
                >
                  <span
                    className={`h-3 w-3 rounded-full border transition-all duration-500 ${
                      isActive
                        ? "bg-[#D4AF37] border-[#D4AF37] scale-125 shadow-[0_0_16px_rgba(212,175,55,0.6)]"
                        : "bg-[#0A1420] border-white/40 group-hover:border-[#D4AF37]"
                    }`}
                  />
                  <span
                    className={`mt-4 font-serif-lux text-lg sm:text-xl transition-colors duration-300 ${
                      isActive ? "gold-gradient-text" : "text-[#A0ABC0] group-hover:text-white"
                    }`}
                  >
                    {m.year}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <div className="mt-12 min-h-[240px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start border-t border-[#D4AF37]/20 pt-12"
              data-testid="chronicle-detail"
            >
              <div className="lg:col-span-4">
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em]">
                  {current.principle}
                </p>
                <h3 className="mt-4 font-serif-lux text-3xl sm:text-4xl text-white">
                  {current.title}
                </h3>
              </div>
              <div className="lg:col-span-8">
                <p className="font-serif-lux italic text-[#D4AF37]/40 text-6xl sm:text-7xl leading-none">
                  {current.year}
                </p>
                <p className="mt-6 text-[#A0ABC0] text-lg sm:text-xl font-light leading-relaxed max-w-2xl">
                  {current.text}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
